import { useSyncExternalStore } from "react";
import { clearRecordPrepare } from "@/features/forms/shared/record/record-prepare";
import { currentSession } from "@/shared/api/session";

export type PortalWork = PortalRecordWork | PortalPickerWork;

export interface PortalRecordWork {
  kind: "record";
  path: string;
  recordId: string;
  scrollTop: number;
}

export interface PortalPickerWork {
  kind: "picker";
  path: string;
  choices: PickerChoices;
  prepareId: string | null;
}

export interface PickerChoices {
  client: string;
  alternative: string;
  workflow: WorkflowChoices | null;
}

export type WorkflowChoices = RecordWorkflowChoices | SignDocumentWorkflowChoices;

export interface RecordWorkflowChoices {
  kind: "record";
  family: string;
  portfolioId: string;
  meetingFormat: string;
  sourceValue: string;
  way: string;
  priorSourceValue: string;
  continuedRecordId: string;
}

export interface SignDocumentWorkflowChoices {
  kind: "sign_document";
  way: string;
  draftId: string;
}

export const RESUME_PORTAL_WORK = { resumePortalWork: true } as const;

export function isPortalWorkResume(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { resumePortalWork?: unknown }).resumePortalWork === true
  );
}

export function usePortalWorkInProgress(): boolean {
  return useSyncExternalStore(subscribe, () => readRaw() !== null);
}

export function closedRecordState(recordId: string): { closedRecord: string } {
  return { closedRecord: recordId };
}

export function closedRecordOf(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const closed = (state as { closedRecord?: unknown }).closedRecord;
  return typeof closed === "string" ? closed : null;
}

export function readPortalWork(): PortalWork | null {
  const raw = readRaw();
  if (raw === null) return null;
  try {
    return portalWorkOf(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function resumedRecordScroll(path: string, state: unknown): number | null {
  if (!isPortalWorkResume(state)) return null;
  const work = readPortalWork();
  return work?.kind === "record" && work.path === path ? work.scrollTop : null;
}

export function resumedPickerChoices(state: unknown): PickerChoices | null {
  if (!isPortalWorkResume(state)) return null;
  const work = readPortalWork();
  return work?.kind === "picker" ? work.choices : null;
}

export function rememberPortalRecord(recordId: string, path: string, scrollTop: number): void {
  rememberPortalWork({ kind: "record", path, recordId, scrollTop });
}

export function rememberRecordScroll(recordId: string, scrollTop: number): void {
  const work = readPortalWork();
  if (work?.kind !== "record" || work.recordId !== recordId || work.scrollTop === scrollTop) return;
  rememberPortalWork({ ...work, scrollTop });
}

export function rememberPickerChoices(path: string, choices: PickerChoices): void {
  rememberPortalWork({ kind: "picker", path, choices, prepareId: null });
}

export function rememberPortalPrepare(path: string, prepareId: string, client: string): void {
  const work = readPortalWork();
  const choices =
    work?.kind === "picker" && work.choices.client === client
      ? work.choices
      : { client, alternative: "advice", workflow: null };
  rememberPortalWork({ kind: "picker", path, choices, prepareId });
}

export function portalPrepareParked(prepareId: string): boolean {
  const work = readPortalWork();
  return work?.kind === "picker" && work.prepareId === prepareId;
}

export function forgetPortalRecord(recordId: string): void {
  const work = readPortalWork();
  if (work?.kind === "record" && work.recordId === recordId) forgetPortalWork();
}

export function forgetPortalPicker(): void {
  if (readPortalWork()?.kind === "picker") forgetPortalWork();
}

export function cancelPortalPrepare(): void {
  clearRecordPrepare();
  forgetPortalPicker();
}

function rememberPortalWork(work: PortalWork): void {
  const key = storageKey();
  if (key === null) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(work));
  } catch {}
  changed();
}

function forgetPortalWork(): void {
  const key = storageKey();
  if (key === null) return;
  try {
    window.localStorage.removeItem(key);
  } catch {}
  changed();
}

function readRaw(): string | null {
  const key = storageKey();
  if (key === null) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function subscribe(listener: () => void): () => void {
  LISTENERS.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    LISTENERS.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

function changed(): void {
  for (const listener of [...LISTENERS]) listener();
}

function storageKey(): string | null {
  const userId = currentSession()?.userId;
  return userId ? `${PORTAL_WORK_KEY}.${userId}` : null;
}

function portalWorkOf(value: unknown): PortalWork | null {
  if (typeof value !== "object" || value === null) return null;
  const work = value as Record<string, unknown>;
  const { path, recordId, scrollTop } = work;
  if (typeof path !== "string") return null;
  if (work.kind === "record") {
    return typeof recordId === "string" && typeof scrollTop === "number"
      ? { kind: "record", path, recordId, scrollTop }
      : null;
  }
  if (work.kind === "picker") {
    const choices = pickerChoicesOf(work.choices);
    return choices === null
      ? null
      : {
          kind: "picker",
          path,
          choices,
          prepareId: typeof work.prepareId === "string" ? work.prepareId : null,
        };
  }
  return null;
}

function pickerChoicesOf(value: unknown): PickerChoices | null {
  if (typeof value !== "object" || value === null) return null;
  const choices = value as Partial<Record<keyof PickerChoices, unknown>>;
  if (typeof choices.client !== "string" || typeof choices.alternative !== "string") return null;
  return {
    client: choices.client,
    alternative: choices.alternative,
    workflow: workflowChoicesOf(choices.workflow),
  };
}

function workflowChoicesOf(value: unknown): WorkflowChoices | null {
  if (typeof value !== "object" || value === null) return null;
  const choices = value as Record<string, unknown>;
  const text = (field: string) => {
    const read = choices[field];
    return typeof read === "string" ? read : "";
  };
  if (choices.kind === "record") {
    return {
      kind: "record",
      family: text("family"),
      portfolioId: text("portfolioId"),
      meetingFormat: text("meetingFormat"),
      sourceValue: text("sourceValue"),
      way: text("way"),
      priorSourceValue: text("priorSourceValue"),
      continuedRecordId: text("continuedRecordId"),
    };
  }
  if (choices.kind === "sign_document") {
    return { kind: "sign_document", way: text("way"), draftId: text("draftId") };
  }
  return null;
}

const PORTAL_WORK_KEY = "simon.advicePortal.work";

const LISTENERS = new Set<() => void>();
